import { H3, StyledAlert } from "App/global-style";
import {
  getFormFieldIdWithProductSuffix,
  useTexts,
} from "Components/TextContext/";
import {
  Button,
  Placeholder,
} from "Components/TextContext/commonComponentEnums";
import { Formik, FormikProps } from "formik";
import React, { useState } from "react";
import { AdditionalBuildingsWrap, FlexWrap } from "../../houseProductsStyled";
import {
  AdditionalBuildingFormValues,
  AdditionalBuildingType,
} from "../additionalBuildingsInterfaces";

import { FormGroupWrap } from "Components/Features/CheckPrice/Products/Advice/ProductDetails/FormElements/styled";
import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { AlertType } from "Components/Form/Alert";
import { Input } from "Components/Form/Input";
import { InputMode } from "Components/Form/Input/inputTypes";
import { Select } from "Components/Form/Select";
import { TextComponentNameLevel1 } from "Components/TextContext/textConstants";
import { ThemedButton } from "Components/Themes/components/ThemedButton";
import { buttonTrackId } from "Utils/analytics";
import { checkIfTargetValueIsANumberAndReturnCurrentValueIfNot } from "Utils/formUtils";
import { getError } from "Utils/productUtils";
import { mapFromFMSBuildingType } from "../additionalBuildingsUtils";
import { additionalBuildingValidationSchema } from "../additionalBuildingsValidation";
import { AnnexFormFields } from "./AnnexFormFields";

interface Props {
  addAdditionalBuilding: (formProps: any) => void;
  setAdditionalBuildings: (value: boolean) => void;
  product: ProductBackendName;
  error?: boolean;
}

export const AddBuilding = ({
  addAdditionalBuilding,
  setAdditionalBuildings,
  product,
  error,
}: Props): React.ReactElement => {
  const [constructionYear, setConstructionYear] = useState("");
  const { getText, getSelectOptions } = useTexts();

  const buildingTypeOptions = getSelectOptions(
    TextComponentNameLevel1.ADDITIONAL_BUILDINGS,
    getFormFieldIdWithProductSuffix(product, "additionalBuilding_buildingType"),
    getText(Placeholder.CHOOSE_TYPE),
  ).filter(
    (option) => option.value !== AdditionalBuildingType.GARAGE_OUTHOUSE_ANNEX,
  );

  return (
    <Formik
      initialValues={{}}
      enableReinitialize
      validationSchema={additionalBuildingValidationSchema()}
      onSubmit={addAdditionalBuilding}
      validateOnMount
    >
      {(formProps: FormikProps<AdditionalBuildingFormValues>) => (
        <FormGroupWrap>
          <AdditionalBuildingsWrap>
            <FlexWrap>
              <H3>
                {getText("additionalbuildings.subheaders.addnewbuilding")}
              </H3>
              <ThemedButton
                variant="icon"
                iconRight="close"
                autoFocus
                trackId={buttonTrackId("exitaddbuilding")}
                aria-label={getText(
                  "additionalbuildings.arialabel.exitaddbuilding",
                )}
                onClick={() => setAdditionalBuildings(false)}
              />
            </FlexWrap>

            <Select
              large
              id="additionalBuilding_buildingType"
              name="additionalBuilding_buildingType"
              label={getText("additionalbuildings.label.buildingtype")}
              options={buildingTypeOptions}
              error={getError("additionalBuilding_buildingType", formProps)}
            />
            <Input
              large
              id="additionalBuilding_areal"
              name="additionalBuilding_areal"
              label={getText("additionalbuildings.label.bta")}
              error={getError("additionalBuilding_areal", formProps)}
              type={"number"}
              inputMode={InputMode.NUMERIC}
            />
            <Input
              large
              id="additionalBuilding_constructionYear"
              name="additionalBuilding_constructionYear"
              label={getText("additionalbuildings.label.constructionyear")}
              error={getError("additionalBuilding_constructionYear", formProps)}
              onChange={(e) => {
                e.target.value =
                  checkIfTargetValueIsANumberAndReturnCurrentValueIfNot(
                    constructionYear,
                    e.target.value,
                    formProps.values.additionalBuilding_constructionYear,
                  );
                setConstructionYear(e.target.value);
              }}
              inputProps={{ maxLength: 4 }}
              inputMode={InputMode.NUMERIC}
            />
            {mapFromFMSBuildingType(
              formProps.values.additionalBuilding_buildingType,
            ) === AdditionalBuildingType.ANNEX && (
              <AnnexFormFields formProps={formProps} />
            )}
            <ThemedButton
              type="button"
              variant="outlined"
              onClick={() => formProps.submitForm()}
              id="submitAdditionalBuildingButton"
              trackId="submitAdditionalBuildingButton"
            >
              {getText(Button.SAVE)}
            </ThemedButton>
          </AdditionalBuildingsWrap>
          {error && (
            <StyledAlert
              type="error"
              alertId="additionalBuilding"
              alertType={AlertType.INPUT_ERROR_ALERT}
              title={getText(
                "additionalbuildings.alerttitle.didyousavebuilding",
              )}
              fluid
            />
          )}
        </FormGroupWrap>
      )}
    </Formik>
  );
};
